import { ReactComponent as SummaryIcon } from '@assets/icons/V4/session-details/summary-icon.svg';
import { useAuthState } from '@contexts/AuthContext';
import { useAnalytics } from 'apps/agora/src/contexts/AnalyticsContext';
import { ANALYTICS_EVENT_NAMES } from 'apps/agora/src/utils/constants';
import { convertHexToRGBA } from 'apps/agora/src/utils/helpers';
import SessionDetailsEmptyState from '../components/SessionDetailsEmptyState';
import { useContext } from 'react';
import { SessionDetailsContext } from '../context/SessionDetailsContext';
import { useTranslation } from 'apps/agora/src/TranslationContext';

interface SessionSummaryCardProps {
  sessionSummary?: string;
  accentColor: string;
  needsFeedback: boolean;
  setIsMentorFeedbackOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const SessionSummaryCard = (props: SessionSummaryCardProps) => {
  const { sessionSummary, accentColor, needsFeedback, setIsMentorFeedbackOpen } = props;
  const { t } = useTranslation();

  const { isMentor: isMentorAuth } = useAuthState();
  const { isParentView } = useContext(SessionDetailsContext);
  const shouldDisplay = isMentorAuth && !isParentView;

  const { trackEvent } = useAnalytics();

  const getEmptyStateText = () => {
    if (!needsFeedback) return t('sessionDetails.noSummary');

    return shouldDisplay
      ? t('sessionDetails.mentorFeedback.noFeedbackWithWarning')
      : t('sessionDetails.mentorFeedback.noFeedbackByMentor');
  };

  const submitFeedbackClickHandler = () => {
    if (!shouldDisplay) {
      return;
    }

    setIsMentorFeedbackOpen?.(true);
    trackEvent(ANALYTICS_EVENT_NAMES.SESSION_SUBMIT_FEEDBACK_BUTTON_CLICK);
  };

  return (
    <section
      className="flex flex-col gap-6 py-6 px-8 rounded-lg "
      style={{ backgroundColor: convertHexToRGBA(accentColor, 50) }}
    >
      <div className="flex gap-4">
        <SummaryIcon className="text-white" />
        <h3 className="font-raleway font-bold text-xl">
          {t('sessionDetails.mentorFeedback.sessionSummary')}
        </h3>
      </div>

      {sessionSummary ? (
        <p className="text-sm text-white leading-5 font-semibold break-words">{sessionSummary}</p>
      ) : (
        <SessionDetailsEmptyState
          icon={<SummaryIcon className="text-white" />}
          textClassName="text-white"
          text={getEmptyStateText()}
          buttonText={
            shouldDisplay && needsFeedback
              ? t('sessionDetails.mentorFeedback.submitFeedback')
              : undefined
          }
          onClick={submitFeedbackClickHandler}
        />
      )}
    </section>
  );
};

export default SessionSummaryCard;
